import { useState, useEffect } from 'react';
import './index.css';
import Login           from './Login';
import TimeTracker     from './TimeTracker';
import Report          from './Report';
import Projects        from './Projects';
import Invoices        from './Invoices';
import UpworkDashboard from './UpworkDashboard';
import UpworkSetup     from './UpworkSetup';
import QuickBooksSetup from './QuickBooksSetup';
import { api } from './api';

export default function App() {
  const [booting,   setBooting]   = useState(true);
  const [authed,    setAuthed]    = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [bypass,    setBypass]    = useState(false);
  const [demo,      setDemo]      = useState(false);
  const [logoutUrl, setLogoutUrl] = useState('/.auth/logout?post_logout_redirect_uri=/');
  // Land on the Setup tab when returning from the Upwork OAuth callback
  // (/?upwork_connected=1) so the user sees the success banner.
  const [tab,       setTab]       = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('upwork_connected') === '1') return 'setup';
    if (qs.get('qbo_connected')    === '1') return 'books';
    return 'tracker';
  });
  const [projects,  setProjects]  = useState([]);
  // Plan entitlement — gates Pro (Upwork/AI/Outlook) and Complete (QuickBooks).
  const [ent,       setEnt]       = useState({ tier: 'free', is_pro: false, is_complete: false });

  // On mount: ask server who we are. 200 → authed, 401 → show login.
  // Three response shapes the server can send:
  //   { email, bypass:true  }            → local dev with AUTH_DEV_BYPASS
  //   { email, demo:true    }            → public marketing demo (uptracker-demo)
  //   { email, logout_url, ... }         → real BPS handoff session
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
          const info = await res.json();
          setUserEmail(info.email);
          setBypass(!!info.bypass);
          setDemo(!!info.demo);
          if (info.logout_url) setLogoutUrl(info.logout_url);
          setAuthed(true);
        }
      } catch {
        /* server unreachable — treat as unauth */
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  async function loadProjects() {
    try {
      setProjects(await api.getProjects());
    } catch {
      /* 401 → api.js redirects through /AuthBridge/Login (BPS sign-in) */
    }
  }

  useEffect(() => { if (authed) loadProjects(); }, [authed]);

  // Fetch plan entitlement once authed (free users get gated tabs).
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try { setEnt(await api.getEntitlement()); } catch { /* default free */ }
    })();
  }, [authed]);

  function handleSignOut() {
    // In dev-bypass there's no real session to clear. Reload is a no-op
    // visually; we just don't offer a sign-out button in that mode (below).
    window.location.href = logoutUrl;
  }

  // Boot: still figuring out auth state
  if (booting) {
    return (
      <div className="login-wrap">
        <div className="login-card"><p style={{ color: '#737370' }}>Loading…</p></div>
      </div>
    );
  }

  if (!authed) return <Login />;

  return (
    <div className="app-root">
      {bypass && (
        <div style={{
          background: '#FEF3C7',
          borderBottom: '1px solid #F59E0B',
          color: '#92400E',
          fontSize: 12,
          padding: '6px 12px',
          textAlign: 'center',
          fontFamily: 'monospace',
        }}>
          ⚠ AUTH_DEV_BYPASS active — every request runs as {userEmail}. Not authenticated.
        </div>
      )}

      {demo && (
        <div style={{
          background: '#FFEDD5',
          borderBottom: '1px solid #F97316',
          color: '#9A3412',
          fontSize: 13,
          padding: '8px 12px',
          textAlign: 'center',
        }}>
          <strong>Demo mode</strong> — sample data only. Try any feature freely; the database resets nightly.
          {' '}<a href="https://bps-corp.com/Products" style={{ color: '#9A3412', textDecoration: 'underline' }}>Back to products</a>
        </div>
      )}

      <header className="app-header">
        <div className="app-header-left">
          <div className="brand-mark">U</div>
          <div className="brand-text">
            <h1 className="brand-title">Up-Tracker</h1>
            <div className="brand-sub">Time &amp; Upwork tracking</div>
          </div>
        </div>
        <div className="app-header-right">
          {userEmail && <span className="user-email">{userEmail}</span>}
          <span className={`plan-badge plan-${ent.tier}`}>{ent.tier === 'complete' ? 'Complete' : ent.tier === 'pro' ? 'Pro' : 'Free'}</span>
          {!bypass && !demo && (
            <button className="btn-outline" style={{ fontSize: 13 }} onClick={handleSignOut}>
              Sign out
            </button>
          )}
        </div>
      </header>

      <main className="app-main">
        <div className="app-panel">
          <div className="tabs">
            <button className={`tab${tab === 'tracker'  ? ' active' : ''}`} onClick={() => setTab('tracker')}>Timesheet</button>
            <button className={`tab${tab === 'report'   ? ' active' : ''}`} onClick={() => setTab('report')}>Report</button>
            <button className={`tab${tab === 'projects' ? ' active' : ''}`} onClick={() => setTab('projects')}>Projects</button>
            <button className={`tab${tab === 'invoices' ? ' active' : ''}`} onClick={() => setTab('invoices')}>Invoices</button>
            <button className={`tab${tab === 'upwork'   ? ' active' : ''}`} onClick={() => setTab('upwork')}>Upwork</button>
            <button className={`tab${tab === 'books'    ? ' active' : ''}`} onClick={() => setTab('books')}>Books</button>
            <button className={`tab${tab === 'setup'    ? ' active' : ''}`} onClick={() => setTab('setup')}>Setup</button>
            <a className="tab" href="https://bps-corp.com/Guides/up-tracker" target="_blank" rel="noopener" title="How to use Up-Tracker — opens the user guide">Docs</a>
          </div>

          {tab === 'tracker'  && <TimeTracker projects={projects} onProjectsChange={loadProjects} />}
          {tab === 'report'   && <Report      projects={projects} canEmail={ent.is_pro} />}
          {tab === 'projects' && <Projects    projects={projects} onProjectsChange={loadProjects} />}
          {tab === 'invoices' && <Invoices    projects={projects} />}
          {tab === 'upwork'   && (ent.is_pro ? <UpworkDashboard /> : <Upgrade feature="the Upwork dashboard" need="Pro" />)}
          {tab === 'books'    && (ent.is_complete ? <QuickBooksSetup /> : <Upgrade feature="QuickBooks books" need="Complete" />)}
          {tab === 'setup'    && (ent.is_pro ? <UpworkSetup /> : <Upgrade feature="Upwork sync" need="Pro" />)}
        </div>
      </main>
    </div>
  );
}

// Shown in place of a gated feature when the user's plan is too low.
function Upgrade({ feature, need }) {
  const url = `https://bps-corp.com/Products/UpTracker#pricing`;
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1.5rem', maxWidth: 460, margin: '0 auto' }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, margin: '0 auto 14px',
        background: 'var(--accent-soft)', color: 'var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
      }}>🔒</div>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Upgrade to {need}</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
        {feature} is part of the <strong>{need}</strong> plan. Upgrade to unlock it — every paid plan
        includes a 14-day free trial.
      </p>
      <a className="btn-primary" href={url} target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>
        See plans &amp; upgrade →
      </a>
    </div>
  );
}
